import { canAutoReload } from './stale-client-reload';
import { hasReloadBudget, spendReloadBudget } from './reload-budget';
import { isCloudflareChallengeResponse } from './is-cloudflare-challenge';

/** sessionStorage key recording the last Cloudflare-challenge hard-reload (epoch ms). */
const RELOAD_GUARD_KEY = 'archipro:cloudflare-challenge-reload-at';

/** sessionStorage key counting challenge reloads spent in the current window. */
const RELOAD_BUDGET_KEY = 'archipro:cloudflare-challenge-reload-count';

export type TryReloadOnCloudflareChallengeOptions = {
    /**
     * When false, skip the hard reload (caller should show manual refresh).
     * Used for document/SSR-origin errors and action-originated 403s.
     */
    allowAutoReload?: boolean;
};

/**
 * Recover from a Cloudflare bot challenge that landed on a Remix soft-nav
 * (`.data`) fetch as an HTTP 403 ErrorResponse.
 *
 * Soft-nav already updated the browser URL; a full document reload lets
 * Cloudflare serve its own challenge page (with a working CTA) instead of our
 * Page403 “You need access” UI, which has no way to complete the challenge.
 *
 * The cooldown guard (via sessionStorage) stops an infinite reload loop when
 * the challenge keeps failing. If sessionStorage is unavailable we decline to
 * reload rather than risk a loop.
 *
 * @returns true if a reload was triggered — the caller should show a recovery
 *          placeholder instead of Page403 while the document unloads.
 */
export const tryReloadOnCloudflareChallenge = (
    status: number,
    data: unknown,
    options: TryReloadOnCloudflareChallengeOptions = {},
): boolean => {
    if (options.allowAutoReload === false) return false;
    if (typeof window === 'undefined') return false;
    if (!isCloudflareChallengeResponse(status, data)) return false;

    let lastReloadAt: number;
    try {
        lastReloadAt = Number(
            window.sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0,
        );
    } catch {
        return false;
    }

    const now = Date.now();
    if (!canAutoReload(now, lastReloadAt)) return false;
    // Checked before the cooldown is charged, spent once the reload is certain.
    if (!hasReloadBudget(RELOAD_BUDGET_KEY)) return false;

    try {
        window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
    } catch {
        return false;
    }

    if (!spendReloadBudget(RELOAD_BUDGET_KEY)) return false;

    console.warn(
        '[CatchPage] Cloudflare bot challenge on soft-nav — hard-reloading so the challenge page can run',
    );
    window.location.reload();
    return true;
};
